// ------------------------------------------------------------------------------
// <copyright file="ManualQaAdminWorkspace.jsx" company="C2HL">
//     Copyright (c) 2024 C2HL. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import homePageV2Plan from '../data/manualQaHomePageV2TestPlan.json';
import iosBillingPlan from '../data/manualQaIosBillingTestPlan.json';
import qolsyncExploratoryPlan from '../data/manualQaQolsyncExploratoryTestPlan.json';
import qolsyncPhase2T1Plan from '../data/manualQaQolsyncPhase2T1TestPlan.json';
import qolsyncPhase2T2Plan from '../data/manualQaQolsyncPhase2T2TestPlan.json';
import manualQaApiService from '../services/manualQaApiService';
import { trackException } from '../services/appInsightsService';
import {
  buildManualQaHoursCsv,
  buildManualQaReportsCsv,
  downloadManualQaCsv,
} from '../utils/manualQaAdminExport';
import { findManualQaCardContext } from '../utils/manualQaCardContext';
import './ManualQaAdminWorkspace.css';

const MASTER_TENANT_ID = manualQaApiService.MANUAL_QA_MASTER_TENANT_ID || 'c2hl';
const DEFAULT_CATALOG_ID = 'iosclient-view-plan-v1';

const NAV_ITEMS = [
  { id: 'command', label: 'Command Center' },
  { id: 'campaigns', label: 'Campaign Builder' },
  { id: 'testers', label: 'Tester Management' },
  { id: 'bugs', label: 'Bug Triage' },
  { id: 'reports', label: 'Reports' },
  { id: 'hours', label: 'Hours Review' },
];

const IOS_SCENARIO_IDS = [
  'ios-pim-direct-soniox',
  'ios-plan-entitlement',
  'ios-two-way-conversation',
  'ios-translate-pdf-word-link-text',
  'ios-photo-menu-translate',
  'ios-recents-delete-tags-filter-search',
];

function getUniqueScenarioIds(plan) {
  return Array.from(new Set((plan.views || []).map(view => view.parentScenarioId || view.id)));
}

const CATALOG_OPTIONS = [
  {
    id: DEFAULT_CATALOG_ID,
    label: 'iOSClient TestFlight',
    campaignName: 'TestFlight 2.1 Build 482',
    buildLabel: 'iOS 2.1 (482)',
    evidence: 'Device evidence required for TestFlight runs.',
    scenarioIds: IOS_SCENARIO_IDS,
  },
  {
    id: homePageV2Plan.catalogId,
    label: 'HomePage V2 Web QA',
    campaignName: 'HomePage V2 Web Funnel',
    buildLabel: 'Browser matrix',
    evidence: 'Browser evidence only; not TestFlight/device evidence.',
    scenarioIds: getUniqueScenarioIds(homePageV2Plan),
  },
  {
    id: iosBillingPlan.catalogId,
    label: 'iOS Billing (StoreKit sandbox)',
    campaignName: 'iOS Billing Verification',
    buildLabel: 'TestFlight, sandbox account',
    evidence: 'Device evidence required: screenshot or recording of each purchase, receipt, or denial.',
    scenarioIds: getUniqueScenarioIds(iosBillingPlan),
  },
  {
    id: qolsyncExploratoryPlan.catalogId,
    label: 'QolSync Beta — Phase 1 (free testing)',
    campaignName: 'QolSync Beta Phase 1',
    buildLabel: 'TestFlight, any build',
    evidence: 'Device evidence optional; Notes carry the finding.',
    scenarioIds: getUniqueScenarioIds(qolsyncExploratoryPlan),
  },
  {
    id: qolsyncPhase2T1Plan.catalogId,
    label: 'QolSync Beta — Phase 2, Tester 1',
    campaignName: 'QolSync Beta Phase 2 — Tester 1',
    buildLabel: 'TestFlight, current build',
    evidence: 'Device evidence required for capture and export cards.',
    scenarioIds: getUniqueScenarioIds(qolsyncPhase2T1Plan),
  },
  {
    id: qolsyncPhase2T2Plan.catalogId,
    label: 'QolSync Beta — Phase 2, Tester 2',
    campaignName: 'QolSync Beta Phase 2 — Tester 2',
    buildLabel: 'TestFlight, current build',
    evidence: 'Device evidence required for commerce, camera and iPad cards.',
    scenarioIds: getUniqueScenarioIds(qolsyncPhase2T2Plan),
  },
];

// Unified Plan Catalog vocabulary and channel-agnostic plan ids
// (~/.claude/plans/golden-napping-planet.md §0-1) — Plus/Pro and the plan_ios_* prefix are
// retired; live.cap for Free is 0 per D25 (Free is zero-usage on every axis).
const PLAN_ROWS = [
  { tier: 'Free', planId: 'plan_free_v2', assertion: 'live.cap=0, TTS off' },
  { tier: 'Meeting', planId: 'plan_meeting_v2', assertion: 'live.cap=300, PDF export' },
  { tier: 'Meeting Pro', planId: 'plan_meeting_pro_v2', assertion: 'live.cap=1200, DOCX export' },
];

function triageToneClass(verdict) {
  if (verdict === 'file') return 'mq-admin-badge-green';
  if (verdict === 'duplicate' || verdict === 'notADefect') return 'mq-admin-badge-red';
  return 'mq-admin-badge-amber';
}

function statusClass(status) {
  if (/active|approved|accepted/i.test(status)) return 'mq-admin-badge-green';
  if (/blocker|needs reason/i.test(status)) return 'mq-admin-badge-red';
  if (/must|pending|needs review|high/i.test(status)) return 'mq-admin-badge-amber';
  return '';
}

function toDisplayStatus(value) {
  if (!value) return 'Unknown';
  return String(value)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^\w/, letter => letter.toUpperCase());
}

function getTesterName(tester) {
  return tester.name || tester.displayName || tester.testerUserId || 'Unknown tester';
}

function getTesterEmail(tester) {
  return tester.email || tester.Email || 'No email recorded';
}

function getTesterGithub(tester) {
  const username = tester.github || tester.githubUsername || '';
  if (!username) return 'not set';
  return username.startsWith('@') ? username : `@${username}`;
}

function getTesterId(tester) {
  return tester.testerUserId || tester.id;
}

function getAssignmentCampaign(assignment) {
  if (!assignment) return 'Unassigned';
  const tier = assignment.planTier || 'Assigned';
  const catalog = assignment.catalogId || 'manual QA';
  return `${tier} / ${catalog}`;
}

function getRunId(row) {
  return row.runId || String(row.id || '').replace(/^hoursReview_/, '').replace(/^run_/, '');
}

// A past due date makes IsActiveAssignment false, and the server then rejects every tester
// write with 403 -- mid-run, with no warning to the tester beyond a soft "saved locally"
// status. The QolSync beta paths run two weeks, so a +7d default would have cut testers off
// on day 8. Default to +28d and let the admin shorten it deliberately.
const DEFAULT_ASSIGNMENT_WINDOW_DAYS = 28;

function defaultDueDateIso() {
  // Anchor to UTC midnight today so the date-only picker (which slices the ISO to yyyy-mm-dd)
  // shows a stable window regardless of the admin's local time-of-day.
  const now = new Date();
  const utcMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return new Date(utcMidnight + DEFAULT_ASSIGNMENT_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
}

function getCatalogSurface(catalogId) {
  if (!catalogId) return 'Manual QA';
  return catalogId.startsWith('homepage') ? 'Web V2' : 'iOS TestFlight';
}

/**
 * Percentage of a run's cards that carry a verdict.
 *
 * Runs autosave `stepResults` from the first tick onward, so this is available
 * continuously -- unlike the completion report, which only exists once a tester
 * presses Complete run. Steps are grouped back into cards via the
 * `<viewId>-step-<n>` id, and a card counts as done once its status leaves
 * `notRun`.
 *
 * @param {object} run - Persisted Manual QA run document.
 * @returns {number} Whole-number percentage, 0-100.
 */
function getRunProgress(run) {
  const steps = run?.stepResults || [];
  if (steps.length === 0) {
    return run?.status === 'completed' ? 100 : 0;
  }

  const doneByCard = new Map();
  steps.forEach((step) => {
    const card = String(step.stepId || '').replace(/-step-\d+$/, '') || step.scenarioId || '';
    const done = (step.status || 'notRun') !== 'notRun';
    doneByCard.set(card, doneByCard.get(card) || done);
  });

  if (doneByCard.size === 0) return 0;
  const done = Array.from(doneByCard.values()).filter(Boolean).length;
  return Math.round((done / doneByCard.size) * 100);
}

function buildCampaignRows(assignments, runs, testers) {
  // Pre-index runs AND tester names by testerUserId once (O(runs + testers)) so each assignment is
  // an O(1) lookup — keeps the Command Center O(assignments + runs + testers), not O(assignments * …).
  const runsByTester = new Map();
  runs.forEach((run) => {
    const list = runsByTester.get(run.testerUserId) || [];
    list.push(run);
    runsByTester.set(run.testerUserId, list);
  });
  const nameByTester = new Map();
  (testers || []).forEach((tester) => {
    nameByTester.set(getTesterId(tester), getTesterName(tester));
  });

  return assignments.map((assignment) => {
    const testerRuns = runsByTester.get(assignment.testerUserId) || [];
    const hasCompleted = testerRuns.some(run => run.status === 'completed');
    // Was a 0/50/100 constant, so one card done and nineteen done both read 50%.
    const progress = hasCompleted
      ? 100
      : testerRuns.reduce((best, run) => Math.max(best, getRunProgress(run)), 0);
    const bugCount = testerRuns.reduce((total, run) => total + Number(run.bugCount || 0), 0);
    return {
      key: assignment.assignmentId || assignment.id || assignment.testerUserId,
      campaign: getAssignmentCampaign(assignment),
      tester: nameByTester.get(assignment.testerUserId) || assignment.testerUserId || 'Unknown tester',
      surface: getCatalogSurface(assignment.catalogId),
      progress,
      status: toDisplayStatus(assignment.status || 'notStarted'),
      bugCount,
    };
  });
}

function buildTesterRows(testers, assignments) {
  if (!testers || testers.length === 0) return [];
  return testers.map((tester) => {
    const testerId = getTesterId(tester);
    const assignment = assignments.find(item => item.testerUserId === testerId);
    return {
      id: testerId,
      name: getTesterName(tester),
      email: getTesterEmail(tester),
      github: getTesterGithub(tester),
      status: toDisplayStatus(tester.status),
      campaign: getAssignmentCampaign(assignment),
      rate: tester.hourlyRate ? `$${tester.hourlyRate}/hr` : 'Set on profile',
      lastSave: tester.updatedAtUtc || 'No saves yet',
    };
  });
}

function buildHourRows(hoursReviews, testers) {
  if (!hoursReviews || hoursReviews.length === 0) return [];
  return hoursReviews.map((row) => {
    const tester = testers.find(item => getTesterId(item) === row.testerUserId);
    return {
      runId: getRunId(row),
      testerUserId: row.testerUserId,
      tester: tester ? getTesterName(tester) : row.testerUserId,
      campaign: row.assignmentId || row.runId || 'Manual QA run',
      active: Number(row.systemHours || 0),
      elapsed: Number(row.elapsedHours || row.systemHours || 0),
      claimed: Number(row.testerReportedHours || 0),
      approved: Number(row.approvedHours || row.testerReportedHours || row.systemHours || 0),
      status: toDisplayStatus(row.status),
    };
  });
}

function getBugTitle(bug) {
  return bug.title || bug.summary || 'Untitled bug';
}

function getBugLink(bug) {
  return bug.link || bug.scenarioId || bug.stepId || bug.runId || 'Manual QA bug';
}

function getBugSurface(bug) {
  return bug.surface || (bug.scenarioId?.startsWith('ios-') ? 'iOS' : 'Manual QA');
}

function formatHours(value) {
  return `${Number(value || 0).toFixed(2).replace(/\.?0+$/, '')}h`;
}

function AdminTableScroll({ children }) {
  return <div className="mq-admin-table-scroll">{children}</div>;
}

function getTesterNameById(testers, testerUserId) {
  const tester = testers.find(item => getTesterId(item) === testerUserId);
  return tester ? getTesterName(tester) : testerUserId || 'Unknown tester';
}

function buildNotifications(reports, bugs, hours, testers) {
  const notifications = [];

  reports.slice(0, 2).forEach((report) => {
    notifications.push({
      title: 'Run submitted',
      tone: '',
      message: `${getTesterNameById(testers, report.testerUserId)} completed run ${report.runId}. ${report.bugCount || 0} bugs reported.`,
    });
  });

  bugs
    .filter(bug => /blocker|high/i.test(bug.severity || '') || /submitted|pending/i.test(bug.status || ''))
    .slice(0, 2)
    .forEach((bug) => {
      notifications.push({
        title: `${toDisplayStatus(bug.severity || 'Bug')} bug`,
        tone: /blocker|high/i.test(bug.severity || '') ? 'danger' : '',
        message: `${getBugTitle(bug)}. ${getBugLink(bug)}. GitHub ${toDisplayStatus(bug.githubIssueState || 'notCreated')}.`,
      });
    });

  hours
    .filter(row => /pending|needs/i.test(row.status || ''))
    .slice(0, 1)
    .forEach((row) => {
      const claimedHours = row.testerReportedHours ?? row.claimed;
      const systemHours = row.systemHours ?? row.active;
      notifications.push({
        title: 'Hours review',
        tone: '',
        message: `${getTesterNameById(testers, row.testerUserId)}: ${formatHours(claimedHours)} claimed against ${formatHours(systemHours)} system time.`,
      });
    });

  return notifications;
}

/**
 * Test Admin workspace for Manual QA management surfaces.
 */
function ManualQaAdminWorkspace() {
  const [activePanel, setActivePanel] = useState('command');
  const [saveState, setSaveState] = useState('');
  const [adminData, setAdminData] = useState({
    testers: [],
    assignments: [],
    runs: [],
    bugs: [],
    reports: [],
    hoursReviews: [],
    loading: true,
    error: '',
  });
  const [lastLoadedAt, setLastLoadedAt] = useState('');
  const [testerForm, setTesterForm] = useState({
    email: 'new.tester@example.com',
    displayName: 'New Tester',
    githubUsername: '@new-tester',
    hourlyRate: '20.00',
  });
  const [provisionedTester, setProvisionedTester] = useState(null);
  const [assignmentForm, setAssignmentForm] = useState({
    testerUserId: '',
    catalogId: DEFAULT_CATALOG_ID,
    planTier: 'Free',
    dueAtUtc: defaultDueDateIso(),
  });
  const [approvedHours, setApprovedHours] = useState({});

  const loadAdminData = useCallback(async () => {
    setAdminData(current => ({
      ...current,
      loading: true,
      error: '',
    }));

    try {
      const [testers, assignments, runs, bugs, reports, hoursReviews] = await Promise.all([
      manualQaApiService.listAdminTesters(MASTER_TENANT_ID),
      manualQaApiService.listAdminAssignments(MASTER_TENANT_ID),
      manualQaApiService.listAdminRuns(MASTER_TENANT_ID),
      manualQaApiService.listAdminBugs(MASTER_TENANT_ID),
      manualQaApiService.listAdminReports(MASTER_TENANT_ID),
      manualQaApiService.listAdminHoursReviews(MASTER_TENANT_ID),
      ]);
      setAdminData({
        testers: testers || [],
        assignments: assignments || [],
        runs: runs || [],
        bugs: bugs || [],
        reports: reports || [],
        hoursReviews: hoursReviews || [],
        loading: false,
        error: '',
      });
      setLastLoadedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    } catch (error) {
      trackException(error, { errorType: 'ManualQa_AdminReadFailed' });
      setAdminData(current => ({
        ...current,
        loading: false,
        error: error.message || 'Manual QA admin data unavailable',
      }));
    }
  }, []);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  const selectedPlan = useMemo(
    () => PLAN_ROWS.find(row => row.tier === assignmentForm.planTier) || PLAN_ROWS[0],
    [assignmentForm.planTier]
  );

  const selectedCatalog = useMemo(
    () => CATALOG_OPTIONS.find(catalog => catalog.id === assignmentForm.catalogId) || CATALOG_OPTIONS[0],
    [assignmentForm.catalogId]
  );

  const testerRows = useMemo(
    () => buildTesterRows(adminData.testers, adminData.assignments),
    [adminData.assignments, adminData.testers]
  );

  const testerOptions = useMemo(() => {
    const rows = new Map();
    testerRows.forEach(tester => rows.set(tester.id, { id: tester.id, name: tester.name }));
    return Array.from(rows.values());
  }, [testerRows]);

  const bugRows = useMemo(
    () => adminData.bugs,
    [adminData.bugs]
  );

  const reportRows = useMemo(
    () => adminData.reports,
    [adminData.reports]
  );

  const hourRows = useMemo(
    () => buildHourRows(adminData.hoursReviews, adminData.testers),
    [adminData.hoursReviews, adminData.testers]
  );

  useEffect(() => {
    setApprovedHours(current => hourRows.reduce((next, row) => ({
      ...next,
      [row.runId]: next[row.runId] ?? row.approved,
    }), current));
  }, [hourRows]);

  const createTesterLogin = async () => {
    const tester = {
      displayName: testerForm.displayName,
      email: testerForm.email,
      githubUsername: testerForm.githubUsername.trim().replace(/^@+/, ''),
      hourlyRate: testerForm.hourlyRate,
    };

    try {
      setSaveState('Creating tester');
      const result = await manualQaApiService.provisionTester(tester);
      setProvisionedTester(result);
      if (result?.profile) {
        setAdminData(current => ({
          ...current,
          testers: [
            result.profile,
            ...current.testers.filter(item => getTesterId(item) !== getTesterId(result.profile)),
          ],
        }));
      }
      setSaveState(result?.createdLogin ? 'Tester created' : 'Tester profile linked');
    } catch (error) {
      trackException(error, { errorType: 'ManualQa_AdminTesterSaveFailed' });
      setSaveState(error.message || 'Tester login creation failed');
    }
  };

  const saveCampaignAssignment = async () => {
    const assignment = {
      tenantId: MASTER_TENANT_ID,
      // Shared with the runner so the server can authorize that tester's writes.
      assignmentId: manualQaApiService.buildManualQaAssignmentId(
        assignmentForm.testerUserId,
        selectedCatalog.id
      ),
      catalogId: selectedCatalog.id,
      testerUserId: assignmentForm.testerUserId,
      scenarioIds: selectedCatalog.scenarioIds,
      planTier: assignmentForm.planTier,
      targetPlanId: selectedPlan.planId,
      dueAtUtc: assignmentForm.dueAtUtc,
    };

    try {
      setSaveState('Saving assignment draft');
      await manualQaApiService.saveAssignment(assignment);
      setSaveState('Assignment draft saved');
    } catch (error) {
      trackException(error, { errorType: 'ManualQa_AdminAssignmentSaveFailed' });
      setSaveState(error.message || 'Assignment save failed');
    }
  };

  const saveHours = async (row) => {
    try {
      setSaveState('Saving hours review');
      await manualQaApiService.saveHoursReview(row.runId, {
        tenantId: MASTER_TENANT_ID,
        testerUserId: row.testerUserId,
        systemHours: row.active,
        testerReportedHours: row.claimed,
        approvedHours: Number(approvedHours[row.runId] || 0),
        status: 'approved',
        adminNotes: 'Approved by Test Admin after comparing system and claimed hours.',
      });
      setSaveState('Hours review saved');
    } catch (error) {
      trackException(error, { errorType: 'ManualQa_AdminHoursSaveFailed' });
      setSaveState(error.message || 'Hours review save failed');
    }
  };

  const runTriage = async (bug) => {
    if (!bug?.id) {
      setSaveState('Select a submitted bug first');
      return;
    }

    try {
      setSaveState('Running triage');
      const triagedBug = await manualQaApiService.triageBug(
        MASTER_TENANT_ID,
        bug.id,
        findManualQaCardContext(bug, adminData.runs)
      );
      setAdminData(current => ({
        ...current,
        bugs: current.bugs.map(item => (item.id === triagedBug.id ? triagedBug : item)),
      }));
      setSaveState('Triage complete');
    } catch (error) {
      trackException(error, { errorType: 'ManualQa_AdminTriageFailed' });
      setSaveState(error.message || 'Triage failed');
    }
  };

  const createGithubIssue = async (bug) => {
    if (!bug?.id) {
      setSaveState('Select a submitted bug first');
      return;
    }

    try {
      setSaveState('Creating GitHub issue');
      const savedBug = await manualQaApiService.createGithubIssue(MASTER_TENANT_ID, bug.id);
      setAdminData(current => ({
        ...current,
        bugs: current.bugs.map(item => (item.id === savedBug.id ? savedBug : item)),
      }));
      setSaveState('GitHub issue created');
    } catch (error) {
      trackException(error, { errorType: 'ManualQa_AdminGithubIssueCreateFailed' });
      setSaveState(error.message || 'GitHub issue creation failed');
    }
  };

  const exportReportsCsv = useCallback(() => {
    downloadManualQaCsv('manual-qa-submitted-reports.csv', buildManualQaReportsCsv(reportRows));
    setSaveState('Reports CSV exported');
  }, [reportRows]);

  const exportHoursCsv = useCallback(() => {
    downloadManualQaCsv('manual-qa-payable-hours.csv', buildManualQaHoursCsv(hourRows));
    setSaveState('Payable hours CSV exported');
  }, [hourRows]);

  return (
    <div className="mq-admin-shell">
      <aside className="mq-admin-sidebar" aria-label="Manual QA admin navigation">
        <div className="mq-admin-brand">
          <span>QS</span>
          <strong>Manual QA</strong>
        </div>
        <div className="mq-admin-nav-section">Admin</div>
        {NAV_ITEMS.map(item => (
          <button
            key={item.id}
            type="button"
            className={activePanel === item.id ? 'active' : ''}
            onClick={() => setActivePanel(item.id)}
          >
            {item.label}
          </button>
        ))}
      </aside>

      <main className="mq-admin-main">
        <header className="mq-admin-topbar">
          <div>
            <p className="mq-admin-eyebrow">Manual QA / Admin / {NAV_ITEMS.find(item => item.id === activePanel)?.label}</p>
            <h1>{activePanel === 'command' ? 'Test Admin Command Center' : NAV_ITEMS.find(item => item.id === activePanel)?.label}</h1>
            <p className="mq-admin-subtitle">
              C2HL master tenant owned tester operations. Customer-tenant admins cannot manage testers.
            </p>
          </div>
          <div className="mq-admin-actions">
            <span className="mq-admin-badge mq-admin-badge-green">C2HL master tenant</span>
            {saveState && <span className="mq-admin-save-state">{saveState}</span>}
            <a className="mq-admin-button" href="/manual-qa">Open tester runner</a>
          </div>
        </header>

        {adminData.error && <div className="mq-admin-error" role="status">{adminData.error}. Showing local review fallback.</div>}
        {activePanel === 'command' && (
          <CommandCenter
            assignments={adminData.assignments}
            runs={adminData.runs}
            bugs={bugRows}
            reports={adminData.reports}
            hours={hourRows}
            testers={adminData.testers}
            loading={adminData.loading}
            lastLoadedAt={lastLoadedAt}
            onRefresh={loadAdminData}
          />
        )}
        {activePanel === 'campaigns' && (
          <CampaignBuilder
            assignmentForm={assignmentForm}
            onAssignmentFormChange={setAssignmentForm}
            selectedPlan={selectedPlan}
            selectedCatalog={selectedCatalog}
            catalogOptions={CATALOG_OPTIONS}
            testerOptions={testerOptions}
            onSave={saveCampaignAssignment}
          />
        )}
        {activePanel === 'testers' && (
          <TesterManagement
            testers={testerRows}
            testerForm={testerForm}
            provisionedTester={provisionedTester}
            onTesterFormChange={setTesterForm}
            onCreateTesterProfile={createTesterLogin}
          />
        )}
        {activePanel === 'bugs' && (
          <BugTriage
            bugs={bugRows}
            onCreateGithubIssue={createGithubIssue}
            onRunTriage={runTriage}
          />
        )}
        {activePanel === 'reports' && (
          <Reports
            reports={reportRows}
            onExportCsv={exportReportsCsv}
          />
        )}
        {activePanel === 'hours' && (
          <HoursReview
            rows={hourRows}
            approvedHours={approvedHours}
            onApprovedHoursChange={setApprovedHours}
            onSaveHours={saveHours}
            onExportCsv={exportHoursCsv}
          />
        )}
      </main>
    </div>
  );
}

function CommandCenter({ assignments, runs, bugs, reports, hours, testers, loading, lastLoadedAt, onRefresh }) {
  const activeCampaigns = assignments.length;
  const completedRuns = runs.filter(row => row.status === 'completed').length;
  const completionPercent = runs.length === 0 ? 0 : Math.round((completedRuns / runs.length) * 100);
  const openBugs = bugs.filter(bug => !/accepted|rejected|closed|created/i.test(bug.status || '')).length;
  const pendingHours = hours
    .filter(row => /pending|needs/i.test(row.status))
    .reduce((total, row) => total + Number(row.claimed || 0), 0);
  const notifications = buildNotifications(reports, bugs, hours, testers);
  const campaignRows = buildCampaignRows(assignments, runs, testers);

  return (
    <>
      <section className="mq-admin-metrics" aria-label="Manual QA admin metrics">
        <Metric value={loading ? '…' : String(activeCampaigns)} label="Active campaigns" />
        <Metric value={loading ? '…' : `${completionPercent}%`} label="Completion rate" />
        <Metric value={loading ? '…' : String(openBugs)} label="Open bugs" />
        <Metric value={loading ? '…' : `${pendingHours.toFixed(1)}h`} label="Claimed hours pending review" />
      </section>

      <section className="mq-admin-split">
        <div className="mq-admin-panel">
          <h2>Campaigns</h2>
          {campaignRows.length === 0 ? (
            <EmptyState
              title={loading ? 'Loading campaigns…' : 'No campaigns yet'}
              body={loading ? '' : 'Assign a tester to a catalog in Campaign Builder to start a campaign.'}
            />
          ) : (
            <AdminTableScroll>
              <table className="mq-admin-table">
                <thead>
                  <tr><th>Campaign</th><th>Tester</th><th>Surface</th><th>Progress</th><th>Bugs</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {campaignRows.map(row => (
                    <tr key={row.key}>
                      <td><strong>{row.campaign}</strong></td>
                      <td>{row.tester}</td>
                      <td><span className="mq-admin-badge">{row.surface}</span></td>
                      <td><Progress value={row.progress} /></td>
                      <td>{row.bugCount}</td>
                      <td><span className={`mq-admin-badge ${statusClass(row.status)}`}>{row.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableScroll>
          )}
        </div>
        <aside className="mq-admin-panel">
          <div className="mq-admin-panel-heading">
            <div>
              <h2>Notifications</h2>
              {lastLoadedAt && <span>Last refreshed {lastLoadedAt}</span>}
            </div>
            <button type="button" onClick={onRefresh}>Refresh</button>
          </div>
          <div className="mq-admin-stack">
            {notifications.length === 0 ? (
              <EmptyState
                title={loading ? 'Loading activity…' : 'No activity yet'}
                body={loading ? '' : 'Submitted runs, bugs, and hours reviews will appear here.'}
              />
            ) : (
              notifications.map(notification => (
                <div
                  key={`${notification.title}-${notification.message}`}
                  className={`mq-admin-callout ${notification.tone}`}
                >
                  <strong>{notification.title}</strong>
                  <span>{notification.message}</span>
                </div>
              ))
            )}
          </div>
        </aside>
      </section>
    </>
  );
}

function CampaignBuilder({ assignmentForm, onAssignmentFormChange, selectedPlan, selectedCatalog, catalogOptions, testerOptions, onSave }) {
  return (
    <section className="mq-admin-wide-split">
      <aside className="mq-admin-panel">
        <h2>Steps</h2>
        <div className="mq-admin-stack">
          <span className="mq-admin-badge">1 Campaign details</span>
          <span className="mq-admin-badge">2 Tier setup</span>
          <span className="mq-admin-badge">3 Scenario selection</span>
          <span className="mq-admin-badge">4 Tester assignments</span>
          <span className="mq-admin-badge">5 Evidence gates</span>
        </div>
      </aside>
      <div className="mq-admin-stack">
        <div className="mq-admin-panel">
          <h2>Create QA Campaign</h2>
          <div className="mq-admin-form-grid">
            <label>
              <span>Campaign name</span>
              <input value={selectedCatalog.campaignName} readOnly />
            </label>
            <label>
              <span>Build</span>
              <input value={selectedCatalog.buildLabel} readOnly />
            </label>
            <label>
              <span>Catalog</span>
              <select
                aria-label="Catalog"
                value={assignmentForm.catalogId}
                onChange={(event) => onAssignmentFormChange({ ...assignmentForm, catalogId: event.target.value })}
              >
                {catalogOptions.map(catalog => <option key={catalog.id} value={catalog.id}>{catalog.label}</option>)}
              </select>
            </label>
            <label>
              <span>Tester</span>
              <select
                aria-label="Tester"
                value={assignmentForm.testerUserId}
                onChange={(event) => onAssignmentFormChange({ ...assignmentForm, testerUserId: event.target.value })}
              >
                <option value="">{testerOptions.length === 0 ? 'No testers yet — create one first' : 'Select tester'}</option>
                {testerOptions.map(tester => <option key={tester.id} value={tester.id}>{tester.name}</option>)}
              </select>
            </label>
            <label>
              <span>Plan tier</span>
              <select
                aria-label="Plan tier"
                value={assignmentForm.planTier}
                onChange={(event) => onAssignmentFormChange({ ...assignmentForm, planTier: event.target.value })}
              >
                {PLAN_ROWS.map(row => <option key={row.tier}>{row.tier}</option>)}
              </select>
            </label>
            <label>
              <span>Due date</span>
              <input
                aria-label="Due date"
                type="date"
                value={(assignmentForm.dueAtUtc || '').slice(0, 10)}
                onChange={(event) => onAssignmentFormChange({
                  ...assignmentForm,
                  dueAtUtc: event.target.value ? new Date(`${event.target.value}T00:00:00.000Z`).toISOString() : '',
                })}
              />
            </label>
          </div>
          <button
            className="mq-admin-primary"
            type="button"
            onClick={onSave}
            disabled={!assignmentForm.testerUserId}
          >
            Save campaign assignment
          </button>
        </div>
        <div className="mq-admin-panel">
          <h2>Tier to planId mapping</h2>
          <AdminTableScroll>
            <table className="mq-admin-table">
              <thead><tr><th>Tester tier</th><th>Plan ID</th><th>Entitlement assertion</th><th>Setup action</th></tr></thead>
              <tbody>
                {PLAN_ROWS.map(row => (
                  <tr key={row.tier} className={row.tier === selectedPlan.tier ? 'selected' : ''}>
                    <td><span className="mq-admin-badge">{row.tier}</span></td>
                    <td><code>{row.planId}</code></td>
                    <td>{row.assertion}</td>
                    <td>POST /platform/tenant/&lbrace;id&rbrace;/plan</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableScroll>
        </div>
        <div className="mq-admin-panel">
          <h2>Scenario selection</h2>
          <div className="mq-admin-stack">
            <div className="mq-admin-callout">
              <strong>{selectedCatalog.label}</strong>
              <span>{selectedCatalog.scenarioIds.length} scenarios assigned. {selectedCatalog.evidence}</span>
            </div>
            <div className="mq-admin-scenario-list" aria-label="Selected scenarios">
              {selectedCatalog.scenarioIds.map(scenarioId => <span key={scenarioId}>{scenarioId}</span>)}
            </div>
          </div>
        </div>
        <div className="mq-admin-panel">
          <h2>Evidence gates</h2>
          <div className="mq-admin-three">
            <div className="mq-admin-callout"><strong>Device evidence</strong><span>Required for Translate Around Me, Conversation, Recents, and local transcript content.</span></div>
            <div className="mq-admin-callout"><strong>Server evidence</strong><span>Cosmos run docs, entitlement API, host/listener backend flows.</span></div>
            <div className="mq-admin-callout"><strong>Browser evidence</strong><span>HomePage V2 screenshots across route, locale, and viewport matrix.</span></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TesterManagement({ testers, testerForm, provisionedTester, onTesterFormChange, onCreateTesterProfile }) {
  return (
    <section className="mq-admin-split">
      <div className="mq-admin-panel">
        <h2>Tester roster</h2>
        {testers.length === 0 ? (
          <EmptyState
            title="No testers yet"
            body="Create a tester on the right, then invite them to sign in with Google or Microsoft."
          />
        ) : (
          <AdminTableScroll>
            <table className="mq-admin-table">
              <thead><tr><th>Tester</th><th>GitHub tag</th><th>Status</th><th>Assigned campaign</th><th>Rate</th><th>Last save</th></tr></thead>
              <tbody>
                {testers.map(tester => (
                  <tr key={tester.id}>
                    <td><strong>{tester.name}</strong><span>{tester.email}</span></td>
                    <td>{tester.github}</td>
                    <td><span className={`mq-admin-badge ${statusClass(tester.status)}`}>{tester.status}</span></td>
                    <td>{tester.campaign}</td>
                    <td>{tester.rate}</td>
                    <td>{tester.lastSave}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableScroll>
        )}
      </div>
      <aside className="mq-admin-panel">
        <h2>Create tester</h2>
        <div className="mq-admin-stack">
          <label>
            <span>Tester email</span>
            <input
              aria-label="Tester email"
              value={testerForm.email}
              onChange={(event) => onTesterFormChange({ ...testerForm, email: event.target.value })}
            />
          </label>
          <label>
            <span>Display name</span>
            <input
              aria-label="Display name"
              value={testerForm.displayName}
              onChange={(event) => onTesterFormChange({ ...testerForm, displayName: event.target.value })}
            />
          </label>
          <label>
            <span>GitHub username for issue tag</span>
            <input
              aria-label="GitHub username"
              value={testerForm.githubUsername}
              onChange={(event) => onTesterFormChange({ ...testerForm, githubUsername: event.target.value })}
            />
          </label>
          <label>
            <span>Hourly rate</span>
            <input
              aria-label="Hourly rate"
              value={testerForm.hourlyRate}
              onChange={(event) => onTesterFormChange({ ...testerForm, hourlyRate: event.target.value })}
            />
          </label>
          <div className="mq-admin-callout danger"><strong>Sign-in by invite only.</strong><span>No Admin or SuperAdmin role is granted. Tester access is profile plus assignment only.</span></div>
          {provisionedTester?.createdLogin && (
            <div className="mq-admin-callout success">
              <strong>Tester ready</strong>
              <span>Ask this tester to sign in with Google or Microsoft using the email below — no password needed. (No email is sent automatically; share it yourself.)</span>
              <span>Sign-in email: <code>{provisionedTester.signInEmail || provisionedTester.profile?.email || ''}</code></span>
            </div>
          )}
          <button className="mq-admin-primary" type="button" onClick={onCreateTesterProfile}>Create tester</button>
        </div>
      </aside>
    </section>
  );
}

function getBugKey(bug) {
  return bug.id || getBugTitle(bug);
}

/**
 * Advisory triage panel for the selected bug.
 *
 * Everything here is a recommendation. The dimensions stay separate — duplicate, actionability,
 * severity — because collapsing them into one score hides which of them drove the verdict, and
 * those are three different decisions for the person triaging.
 *
 * @param {object} props
 * @param {object} props.triage - Stored triage result, or null if triage has not been run.
 */
function TriagePanel({ triage }) {
  if (!triage) {
    return (
      <div className="mq-admin-callout">
        <strong>Triage</strong>
        <span>Not run for this bug yet. Triage checks it against earlier reports and flags what a developer would have to ask the tester for.</span>
      </div>
    );
  }

  return (
    <div className="mq-admin-callout mq-admin-triage">
      <strong>Triage — advisory only</strong>
      <div className="mq-admin-tags">
        <span className={`mq-admin-badge ${triageToneClass(triage.verdict)}`}>{toDisplayStatus(triage.verdict)}</span>
        <span className="mq-admin-badge">{triage.normalizedSeverity || 'unbanded'}</span>
        {triage.launchBlocking && <span className="mq-admin-badge mq-admin-badge-red">launch-blocking</span>}
        <span className="mq-admin-badge">actionable {triage.actionability}/5</span>
      </div>
      {triage.rationale && <span>{triage.rationale}</span>}
      {triage.missingInformation?.length > 0 && (
        <ul className="mq-admin-triage-list">
          {triage.missingInformation.map(gap => <li key={gap}>{gap}</li>)}
        </ul>
      )}
      {triage.duplicateCandidates?.length > 0 && (
        <ul className="mq-admin-triage-list">
          {triage.duplicateCandidates.map(candidate => (
            <li key={candidate.bugId}>
              {Math.round((candidate.score || 0) * 100)}% overlap with {candidate.summary || candidate.bugId}
              {candidate.githubIssueUrl && (
                <> — <a href={candidate.githubIssueUrl} target="_blank" rel="noreferrer">already filed</a></>
              )}
            </li>
          ))}
        </ul>
      )}
      <span className="mq-admin-triage-provenance">
        {triage.source === 'llm' ? `Rules + ${triage.model || 'model'}` : 'Rules only'}
        {triage.llmUnavailableReason ? ` — ${triage.llmUnavailableReason}` : ''}
        {triage.proposedIssueTitle ? ` · issue title: ${triage.proposedIssueTitle}` : ''}
      </span>
    </div>
  );
}

function BugTriage({ bugs, onCreateGithubIssue, onRunTriage }) {
  const [selectedBugKey, setSelectedBugKey] = useState('');
  const selectedBug = useMemo(
    () => bugs.find(bug => getBugKey(bug) === selectedBugKey) || bugs[0] || {},
    [bugs, selectedBugKey]
  );
  const selectedKey = getBugKey(selectedBug);
  const hasCreatedIssue = selectedBug.githubIssueState === 'created' && selectedBug.githubIssueUrl;
  const hasSelection = Boolean(selectedBug.id);

  if (bugs.length === 0) {
    return (
      <section className="mq-admin-panel">
        <h2>Submitted bugs</h2>
        <EmptyState
          title="No bugs submitted yet"
          body="Bugs testers submit from the runner appear here for triage and GitHub issue creation."
        />
      </section>
    );
  }

  return (
    <section className="mq-admin-split mq-admin-bug-layout">
      <div className="mq-admin-panel">
        <h2>Submitted bugs</h2>
        <AdminTableScroll>
          <table className="mq-admin-table mq-admin-bug-table" aria-label="Submitted bugs">
            <thead><tr><th>Bug</th><th>Surface</th><th>Severity</th><th>Status</th><th>Owner</th></tr></thead>
            <tbody>
              {bugs.map(bug => {
                const key = getBugKey(bug);
                const isSelected = key === selectedKey;
                return (
                  <tr
                    key={key}
                    className={isSelected ? 'mq-admin-row-selected' : ''}
                    aria-selected={isSelected}
                  >
                    <td>
                      <button
                        type="button"
                        className="mq-admin-row-select"
                        onClick={() => setSelectedBugKey(key)}
                      >
                        <strong>{getBugTitle(bug)}</strong>
                        <span>{getBugLink(bug)}</span>
                      </button>
                    </td>
                    <td>{getBugSurface(bug)}</td>
                    <td><span className={`mq-admin-badge ${statusClass(bug.severity)}`}>{toDisplayStatus(bug.severity)}</span></td>
                    <td><span className={`mq-admin-badge ${statusClass(bug.status)}`}>{toDisplayStatus(bug.status)}</span></td>
                    <td>{bug.owner || bug.githubIssueState || 'Unassigned'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </AdminTableScroll>
      </div>
      <aside className="mq-admin-panel" aria-label="Selected bug detail">
        <h2>Selected bug</h2>
        <p><strong>{getBugTitle(selectedBug)}</strong></p>
        <div className="mq-admin-tags">
          <span className={`mq-admin-badge ${statusClass(selectedBug.severity)}`}>{toDisplayStatus(selectedBug.severity || 'unknown')}</span>
          <span className={`mq-admin-badge ${statusClass(selectedBug.status)}`}>{toDisplayStatus(selectedBug.status || 'unknown')}</span>
          <span className="mq-admin-badge">{getBugSurface(selectedBug)}</span>
        </div>
        <BugDetailField label="Expected" value={selectedBug.expectedResult} />
        <BugDetailField label="Actual" value={selectedBug.actualResult || selectedBug.description} />
        <BugDetailField label="Repro steps" value={selectedBug.reproSteps} />
        <div className="mq-admin-callout">
          <strong>Evidence</strong>
          <span>{(selectedBug.evidenceIds || []).length > 0 ? `${selectedBug.evidenceIds.length} file(s) attached` : 'No evidence attached'}</span>
        </div>
        <div className="mq-admin-callout">
          <strong>GitHub issue</strong>
          <span>Created server-side from the accepted bug ID; tester tag {getTesterGithub({ githubUsername: selectedBug.testerGithubUsername })}. The browser never sees the GitHub credential (read from Key Vault).</span>
        </div>
        {hasCreatedIssue && (
          <div className="mq-admin-callout success">
            <strong>GitHub issue created</strong>
            <span>#{selectedBug.githubIssueNumber}</span>
            <a href={selectedBug.githubIssueUrl} target="_blank" rel="noreferrer">Open GitHub issue</a>
          </div>
        )}
        <TriagePanel triage={selectedBug.triage} />
        {selectedBug.triage && selectedBug.triage.verdict !== 'file' && !hasCreatedIssue && (
          // Filing stays possible -- triage advises, it does not gate. But a needsInfo or
          // duplicate report reaching the dev team costs them real time, so say so at the
          // moment of the click rather than leaving it to be read further up the panel.
          <p className="mq-admin-caution">
            Triage says <strong>{toDisplayStatus(selectedBug.triage.verdict)}</strong>. Filing anyway is your call —
            consider going back to the tester first.
          </p>
        )}
        <div className="mq-admin-button-row">
          <button
            type="button"
            onClick={() => onRunTriage(selectedBug)}
            disabled={!hasSelection}
          >
            {selectedBug.triage ? 'Re-run triage' : 'Run triage'}
          </button>
          <button
            className="mq-admin-primary"
            type="button"
            onClick={() => onCreateGithubIssue(selectedBug)}
            disabled={!hasSelection || hasCreatedIssue}
          >
            {hasCreatedIssue ? 'GitHub issue created' : 'Create GitHub issue'}
          </button>
        </div>
      </aside>
    </section>
  );
}

/**
 * Labeled read-only field in the Bug Triage detail pane.
 *
 * @param {object} props
 * @param {string} props.label - Field label.
 * @param {string} [props.value] - Field value; renders an em dash when empty.
 */
function BugDetailField({ label, value }) {
  return (
    <div className="mq-admin-detail-field">
      <span className="mq-admin-detail-label">{label}</span>
      <p>{value && value.trim() ? value : '—'}</p>
    </div>
  );
}

function Reports({ reports, onExportCsv }) {
  const hasReports = reports.length > 0;
  const totalBugs = reports.reduce((total, report) => total + Number(report.bugCount || 0), 0);
  const systemHours = reports.reduce((total, report) => total + Number(report.systemDurationSeconds || 0), 0) / 3600;
  const claimedHours = reports.reduce((total, report) => total + Number(report.testerReportedHours || 0), 0);

  return (
    <>
      <section className="mq-admin-metrics">
        <Metric value={String(reports.length)} label="Reports submitted" />
        <Metric value={String(totalBugs)} label="Bugs found" />
        <Metric value={`${systemHours.toFixed(1)}h`} label="System active time" />
        <Metric value={`${claimedHours.toFixed(1)}h`} label="Claimed time" />
      </section>
      <section className="mq-admin-panel">
        <div className="mq-admin-panel-heading">
          <h2>Submitted run reports</h2>
          <button type="button" onClick={onExportCsv} disabled={!hasReports}>Export report CSV</button>
        </div>
        {hasReports ? (
          <AdminTableScroll>
            <table className="mq-admin-table">
              <thead><tr><th>Run</th><th>Tester</th><th>Evidence</th><th>Bugs</th><th>Claimed hours</th></tr></thead>
              <tbody>
                {reports.map(report => (
                  <tr key={report.id || report.runId}>
                    <td><strong>{report.runId}</strong></td>
                    <td>{report.testerUserId}</td>
                    <td>{report.evidenceCount != null ? `${report.evidenceCount} files` : 'Metadata linked'}</td>
                    <td>{report.bugCount || 0}</td>
                    <td>{Number(report.testerReportedHours || 0)}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableScroll>
        ) : (
          <EmptyState
            title="No reports submitted yet"
            body="When a tester completes a run, its immutable report snapshot appears here for review."
          />
        )}
      </section>
    </>
  );
}

function HoursReview({ rows, approvedHours, onApprovedHoursChange, onSaveHours, onExportCsv }) {
  return (
    <section className="mq-admin-panel">
      <div className="mq-admin-panel-heading">
        <h2>Payable Hours</h2>
        <button type="button" onClick={onExportCsv} disabled={rows.length === 0}>Export payable hours CSV</button>
      </div>
      {rows.length === 0 ? (
        <EmptyState
          title="No hours to review yet"
          body="When a tester completes a run, a payable-hours review is created here comparing system-tracked and claimed time."
        />
      ) : (
      <AdminTableScroll>
        <table className="mq-admin-table">
          <thead><tr><th>Tester</th><th>Campaign</th><th>Active</th><th>Elapsed</th><th>Claimed</th><th>Approved</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.runId} data-testid={`hours-review-row-${row.runId}`}>
                <td>{row.tester}</td>
                <td>{row.campaign}</td>
                <td>{row.active}h</td>
                <td>{row.elapsed}h</td>
                <td>{row.claimed}h</td>
                <td>
                  <input
                    aria-label={`Approved hours for ${row.tester}`}
                    type="number"
                    min="0"
                    step="0.25"
                    value={approvedHours[row.runId] ?? row.approved ?? 0}
                    onChange={(event) => onApprovedHoursChange({
                      ...approvedHours,
                      [row.runId]: event.target.value,
                    })}
                  />
                </td>
                <td><span className={`mq-admin-badge ${statusClass(row.status)}`}>{row.status}</span></td>
                <td><button type="button" onClick={() => onSaveHours(row)}>Approve hours</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminTableScroll>
      )}
    </section>
  );
}

function Metric({ value, label }) {
  return (
    <div className="mq-admin-metric">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

/**
 * Empty-state placeholder shown when a Manual QA admin surface has no real data yet.
 *
 * @param {object} props
 * @param {string} props.title - Headline for the empty state.
 * @param {string} [props.body] - Optional supporting guidance.
 */
function EmptyState({ title, body }) {
  return (
    <div className="mq-admin-empty" role="status">
      <strong>{title}</strong>
      {body ? <span>{body}</span> : null}
    </div>
  );
}

function Progress({ value }) {
  return (
    <div className="mq-admin-progress" aria-label={`${value}% complete`}>
      <span style={{ width: `${value}%` }} />
    </div>
  );
}

export default ManualQaAdminWorkspace;
